import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Users, Trash, Plus, X, PencilSimple, Eye, FilePdf, FileXls, ArrowRight, Check, CalendarCheck, FirstAidKit, Clock, ChartLine, Folder, IdentificationBadge } from '@phosphor-icons/react';
import { LayoutToggle, useLayoutMode, getLayoutContainerProps } from '../components/ui/LayoutToggle';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useSearch } from '../components/VerticalTabs';
import { EmployeeDetail } from '../components/employees/EmployeeDetail';
import { EmployeeFormModal } from '../components/employees/EmployeeFormModal';
import { LeaveTable, LeaveRequestModal, LeaveConfigModal } from '../components/employees/LeaveComponents';
import { getProgressColor } from '../utils/styleHelpers';
import { fmtDateFR } from '../utils/dates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SalaryEvolutionTable = ({ userId }) => {
  const [history, setHistory] = useState([]);
  useEffect(() => {
    if (!userId) return;
    axios.get(`${API_URL}/api/users/${userId}/salary-history`, { withCredentials: true })
      .then(res => setHistory(res.data))
      .catch((e) => { console.error('Erreur historique salaire:', e); });
  }, [userId]);

  if (history.length === 0) return null;

  return (
    <div className="card" style={{ marginTop: '24px' }}>
      <div className="card-header" style={{ backgroundColor: '#E3F2FD' }}>
        <h3 className="card-title" style={{ color: '#1565C0', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ChartLine size={18} /> Evolution du tarif journalier
        </h3>
      </div>
      <table className="data-table" data-testid="salary-evolution-table">
        <thead><tr><th>Date</th><th>Ancien tarif</th><th>Nouveau tarif</th><th>Evolution</th></tr></thead>
        <tbody>
          {history.map((h, i) => {
            const diff = h.daily_rate - h.previous_rate;
            return (
              <tr key={h.changed_at || `hist-${i}`}>
                <td>{fmtDateFR(h.changed_at)}</td>
                <td>{h.previous_rate} euro/jour</td>
                <td style={{ fontWeight: '700' }}>{h.daily_rate} euro/jour</td>
                <td style={{ fontWeight: '700', color: diff >= 0 ? '#2E7D32' : '#C62828' }}>
                  {diff >= 0 ? '+' : ''}{diff} euro
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const JOB_TYPES = [
  'Chef de chantier', 'Conducteur de travaux', 'Macon', 'Electricien',
  'Plombier', 'Peintre', 'Carreleur', 'Menuisier', 'Charpentier',
  'Couvreur', 'Manoeuvre', 'Apprenti', 'Interimaire', 'Autre'
];
const DOC_TYPES = [
  { key: 'ci_recto', label: 'Carte identite (recto)' },
  { key: 'ci_verso', label: 'Carte identite (verso)' },
  { key: 'carte_vitale', label: 'Carte vitale' },
  { key: 'permis_conduire', label: 'Permis de conduire' },
];
const TYPE_CONGE = { conge: { label: 'Conge', color: '#1565C0', bg: '#E3F2FD' } };
const TYPE_MALADIE = {
  maladie: { label: 'Arret maladie', color: '#EF6C00', bg: '#FFF3E0' },
  absence: { label: 'Absence', color: '#5E5F66', bg: '#F4F5F7' },
};
const STATUS_LEAVE = {
  en_attente: { label: 'En attente', color: '#EF6C00', bg: '#FFF3E0' },
  approuve: { label: 'Approuve', color: '#2E7D32', bg: '#E8F5E9' },
  refuse: { label: 'Refuse', color: '#C62828', bg: '#FFEBEE' },
};

const EmployeesPage = ({ embeddedTab }) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { searchTerm } = useSearch();
  const isAdmin = user?.role === 'admin';
  const resolvedTab = embeddedTab === 'salaries_merged' ? 'documents' : embeddedTab;
  const [mainTab, setMainTab] = useState(resolvedTab || (isAdmin ? 'salaries' : 'conges'));
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [layoutMode, setLayoutMode] = useLayoutMode('employees', 'list');
  const [documents, setDocuments] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  // Leaves state
  const [leaves, setLeaves] = useState([]);
  const [leaveConfig, setLeaveConfig] = useState({ annual_days: 25, rtt_days: 0 });
  const [balances, setBalances] = useState([]);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [leaveForm, setLeaveForm] = useState({ type: 'conge', start_date: '', end_date: '', reason: '' });
  const [attestationFile, setAttestationFile] = useState(null);
  const [myBalance, setMyBalance] = useState(null);
  const [periodLabel, setPeriodLabel] = useState('');

  const [form, setForm] = useState({
    name: '', email: '', password: '', job_title: '',
    first_name: '', last_name: '', birth_date: '', nationality: '',
    drivers_license_type: '', social_security_number: '', rib: '',
    entry_date: '', exit_date: '', position: '', qualification: '',
    job_type: '', contract_type: 'CDI', cdd_start_date: '', cdd_end_date: '',
    dpae_done: false, contract_signed: false, caisse_conges: false, mutuelle: false, mutuelle_type: '', daily_rate: 0, hourly_rate: 0,
    net_salary: 0, monthly_gross_salary: 0, benefits: '', admin_notes: '',
    assigned_projects: []
  });
  const [allProjects, setAllProjects] = useState([]);

  const fetchEmployees = useCallback(async () => {
    try {
      if (isAdmin) {
        const res = await axios.get(`${API_URL}/api/users/employees?include_admins=true`, { withCredentials: true });
        setEmployees(res.data);
        const pr = await axios.get(`${API_URL}/api/users/me/projects`, { withCredentials: true });
        setAllProjects(pr.data || []);
      }
    } catch (e) { toast.error('Erreur chargement employes'); }
    finally { setLoading(false); }
  }, [isAdmin, API_URL]);

  const fetchLeaves = useCallback(async (typeFilter) => {
    try {
      let url = `${API_URL}/api/leaves`;
      if (typeFilter) url += `?leave_type=${typeFilter}`;
      const res = await axios.get(url, { withCredentials: true });
      setLeaves(res.data);
    } catch (e) { toast.error('Erreur chargement conges'); }
  }, [API_URL]);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/leaves/config`, { withCredentials: true });
      setLeaveConfig(res.data);
    } catch (e) { console.error('Erreur config conges:', e); }
  }, [API_URL]);

  const fetchBalances = useCallback(async () => {
    try {
      if (isAdmin) {
        const res = await axios.get(`${API_URL}/api/leaves/balance-all`, { withCredentials: true });
        setBalances(res.data.balances || res.data);
        setPeriodLabel(res.data.period_label || '');
      } else {
        const res = await axios.get(`${API_URL}/api/leaves/balance/${user?.id}`, { withCredentials: true });
        setMyBalance(res.data);
        setPeriodLabel(res.data.period_label || '');
      }
    } catch (e) { console.error('Erreur solde conges:', e); }
  }, [isAdmin, user?.id, API_URL]);

  useEffect(() => { fetchEmployees(); fetchConfig(); fetchBalances(); }, [fetchEmployees, fetchConfig, fetchBalances]);
  useEffect(() => {
    if (mainTab === 'conges') fetchLeaves('conge');
    else if (mainTab === 'maladie') fetchLeaves('maladie_absence');
  }, [mainTab, fetchLeaves]);
  useEffect(() => {
    if (embeddedTab) {
      const t = embeddedTab === 'salaries_merged' ? 'documents' : embeddedTab;
      setMainTab(t);
    }
  }, [embeddedTab]);

  const fetchDocs = async (userId) => {
    try {
      const res = await axios.get(`${API_URL}/api/users/${userId}/documents`, { withCredentials: true });
      setDocuments(res.data);
    } catch (e) { console.error('Erreur chargement documents:', e); }
  };

  // Employee CRUD
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode && selectedEmployee) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        delete payload.email;
        await axios.put(`${API_URL}/api/users/${selectedEmployee.id}`, payload, { withCredentials: true });
        toast.success('Employe modifie');
        setSelectedEmployee(null);
      } else {
        await axios.post(`${API_URL}/api/users/employees`, form, { withCredentials: true });
        toast.success('Employe ajoute');
      }
      setShowModal(false);
      setEditMode(false);
      fetchEmployees();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Supprimer ${name} et toutes ses donnees ?`)) return;
    try {
      await axios.delete(`${API_URL}/api/users/${id}`, { withCredentials: true });
      toast.success('Supprime');
      if (selectedEmployee?.id === id) setSelectedEmployee(null);
      fetchEmployees();
    } catch (e) { toast.error('Erreur'); }
  };

  const handleUploadDoc = async (userId, docType, file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API_URL}/api/users/${userId}/documents?doc_type=${docType}`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document televerse');
      fetchDocs(userId);
    } catch (e) { toast.error('Erreur upload'); }
  };

  const handleDeleteDoc = async (userId, docId) => {
    try {
      await axios.delete(`${API_URL}/api/users/${userId}/documents/${docId}`, { withCredentials: true });
      toast.success('Document supprime');
      fetchDocs(userId);
    } catch (e) { toast.error('Erreur'); }
  };

  // Leave CRUD
  const handleCreateLeave = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API_URL}/api/leaves`, leaveForm, { withCredentials: true });
      if (attestationFile && res.data.id) {
        const fd = new FormData();
        fd.append('file', attestationFile);
        await axios.post(`${API_URL}/api/leaves/${res.data.id}/attestation`, fd, { withCredentials: true });
      }
      toast.success('Demande envoyee');
      setShowLeaveModal(false);
      setLeaveForm({ type: mainTab === 'maladie' ? 'maladie' : 'conge', start_date: '', end_date: '', reason: '' });
      setAttestationFile(null);
      if (mainTab === 'conges') fetchLeaves('conge');
      else fetchLeaves('maladie_absence');
      fetchBalances();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleLeaveAction = async (id, status) => {
    try {
      await axios.put(`${API_URL}/api/leaves/${id}`, { status }, { withCredentials: true });
      toast.success('Mis a jour');
      if (mainTab === 'conges') fetchLeaves('conge');
      else fetchLeaves('maladie_absence');
      fetchBalances();
    } catch (err) { toast.error('Erreur'); }
  };

  const handleDeleteLeave = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/leaves/${id}`, { withCredentials: true });
      toast.success('Supprime');
      if (mainTab === 'conges') fetchLeaves('conge');
      else fetchLeaves('maladie_absence');
      fetchBalances();
    } catch (err) { toast.error('Erreur'); }
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/api/leaves/config`, leaveConfig, { withCredentials: true });
      toast.success('Configuration sauvegardee');
      setShowConfigModal(false);
      fetchBalances();
    } catch (err) { toast.error('Erreur'); }
  };

  const openAdd = () => {
    setEditMode(false);
    setForm({ name: '', email: '', password: '', job_title: '', first_name: '', last_name: '', birth_date: '', nationality: '', drivers_license_type: '', social_security_number: '', rib: '', entry_date: '', exit_date: '', position: '', qualification: '', job_type: '', contract_type: 'CDI', cdd_start_date: '', cdd_end_date: '', dpae_done: false, contract_signed: false, caisse_conges: false, mutuelle: false, mutuelle_type: '', daily_rate: 0, hourly_rate: 0, net_salary: 0, monthly_gross_salary: 0, benefits: '', admin_notes: '', assigned_projects: [] });
    setShowModal(true);
  };

  const openEdit = (emp) => {
    setEditMode(true);
    setSelectedEmployee(emp);
    setForm({ ...emp, password: '', assigned_projects: emp.assigned_projects || [] });
    setShowModal(true);
  };

  const selectEmployee = (emp) => {
    setSelectedEmployee(emp);
    fetchDocs(emp.id);
  };

  const badge = (obj, key) => {
    const c = obj[key] || Object.values(obj)[0];
    return <span style={{ padding: '3px 10px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: c.bg, color: c.color }}>{c.label}</span>;
  };

  const checkbox = (label, field) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem' }}>
      <input type="checkbox" checked={form[field] || false} onChange={(e) => setForm({ ...form, [field]: e.target.checked })} style={{ width: '18px', height: '18px' }} />
      {label}
    </label>
  );

  // --- RENDER FUNCTIONS for embedded mode + normal mode ---

  const filterBySearch = (list, fields) => {
    if (!searchTerm) return list;
    const s = searchTerm.toLowerCase();
    return list.filter(item => fields.some(f => (item[f] || '').toString().toLowerCase().includes(s)));
  };
  const filteredEmployees = filterBySearch(employees, ['name', 'first_name', 'last_name', 'email', 'job_type', 'job_title']);
  const filteredLeaves = filterBySearch(leaves, ['user_name', 'reason']);
  const filteredBalances = filterBySearch(balances, ['user_name']);

  const approvedDays = useMemo(() => leaves.filter(l => l.status === 'approuve').reduce((s, l) => s + l.days, 0), [leaves]);
  const maladieDays = useMemo(() => leaves.filter(l => l.type === 'maladie').reduce((s, l) => s + l.days, 0), [leaves]);
  const absenceDays = useMemo(() => leaves.filter(l => l.type === 'absence').reduce((s, l) => s + l.days, 0), [leaves]);

  const renderEmployeeDetail = () => {
    if (!selectedEmployee) return null;
    return (
      <EmployeeDetail
        employee={selectedEmployee}
        documents={documents}
        isAdmin={isAdmin}
        SalaryEvolutionTable={SalaryEvolutionTable}
        onBack={() => setSelectedEmployee(null)}
        onEdit={openEdit}
        onDelete={handleDelete}
        onUploadDoc={handleUploadDoc}
        onDeleteDoc={handleDeleteDoc}
        showModal={showModal}
        renderEmployeeModal={renderEmployeeModal}
      />
    );
  };

  const renderEmployeeCards = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <LayoutToggle layout={layoutMode} setLayout={setLayoutMode} testidPrefix="employees-layout" />
        <div style={{ display: 'flex', gap: 8 }}>
          {filteredEmployees.length > 0 && (
            <>
              <button className="btn btn-secondary" onClick={() => window.open(`${API_URL}/api/users/employees/export/pdf`, '_blank')} data-testid="export-pdf-btn"><FilePdf size={16} /> PDF</button>
              <button className="btn btn-secondary" onClick={() => window.open(`${API_URL}/api/users/employees/badges-pdf`, '_blank')} data-testid="badges-pdf-btn"><IdentificationBadge size={16} /> Planche badges QR</button>
              <button className="btn btn-secondary" onClick={() => window.open(`${API_URL}/api/users/employees/export/excel`, '_blank')} data-testid="export-excel-btn"><FileXls size={16} /> Excel</button>
            </>
          )}
          <button className="btn btn-primary" onClick={openAdd} data-testid="add-employee-btn"><Plus size={16} /> Nouveau salarie</button>
        </div>
      </div>
      {filteredEmployees.length > 0 ? (
        (() => {
          const lp = getLayoutContainerProps(layoutMode, { cardMinWidth: '360px' });
          return (
            <div style={lp.style} className={lp.className || ''} data-testid={`employees-container-${layoutMode}`}>
              {filteredEmployees.map(emp => (
            <div key={emp.id} className="card" style={{ cursor: 'pointer', transition: 'box-shadow 0.2s' }} onClick={() => selectEmployee(emp)} data-testid={`emp-card-${emp.id}`}>
              <div style={{ padding: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {emp.photo_url ? (
                      <img src={`${API_URL}${emp.photo_url}`} alt={emp.name} style={{ width: '42px', height: '42px', borderRadius: '50%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                    ) : null}
                    <div style={{ width: '42px', height: '42px', borderRadius: '50%', backgroundColor: 'var(--primary, #002FA7)', color: 'white', display: emp.photo_url ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '1.1rem' }}>
                      {(emp.first_name || emp.name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: '700', fontSize: '1.05rem' }}>{emp.first_name ? `${emp.first_name} ${emp.last_name || ''}` : emp.name}</div>
                      <div style={{ color: '#5E5F66', fontSize: '0.85rem' }}>{emp.job_type || emp.job_title || 'Non renseigne'}</div>
                    </div>
                  </div>
                  <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: emp.contract_type === 'CDD' ? '#FFF3E0' : '#E3F2FD', color: emp.contract_type === 'CDD' ? '#EF6C00' : '#1565C0' }}>{emp.contract_type}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '0.85rem' }}>
                  <div><span style={{ color: '#5E5F66' }}>Email:</span> {emp.email}</div>
                  <div><span style={{ color: '#5E5F66' }}>Entree:</span> {emp.entry_date || '-'}</div>
                  <div><span style={{ color: '#5E5F66' }}>Tarif/j:</span> {emp.daily_rate ? `${emp.daily_rate}euro` : '-'}</div>
                  <div><span style={{ color: '#5E5F66' }}>Permis:</span> {emp.drivers_license_type || '-'}</div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
                  <span style={{ color: 'var(--primary, #002FA7)', fontSize: '0.85rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>Voir fiche <ArrowRight size={14} /></span>
                </div>
              </div>
            </div>
          ))}
        </div>
          );
        })()
      ) : (
        <div className="card"><div style={{ padding: '40px', textAlign: 'center', color: '#5E5F66' }}><Users size={40} style={{ opacity: 0.5, marginBottom: '12px' }} /><p>Aucun salarie</p></div></div>
      )}
    </div>
  );

  const renderSalariesTable = () => {
    // If user picks a non-list layout, switch to the cards rendering
    if (layoutMode !== 'list') {
      return renderEmployeeCards();
    }
    return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <LayoutToggle layout={layoutMode} setLayout={setLayoutMode} testidPrefix="employees-layout" />
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => window.open(`${API_URL}/api/users/employees/export/pdf`, '_blank')} data-testid="export-pdf-btn"><FilePdf size={16} /> PDF</button>
          <button className="btn btn-secondary" onClick={() => window.open(`${API_URL}/api/users/employees/badges-pdf`, '_blank')} data-testid="badges-pdf-btn"><IdentificationBadge size={16} /> Planche badges QR</button>
          <button className="btn btn-secondary" onClick={() => window.open(`${API_URL}/api/users/employees/export/excel`, '_blank')} data-testid="export-excel-btn"><FileXls size={16} /> Excel</button>
          <button className="btn btn-primary" onClick={openAdd} data-testid="add-employee-btn"><Plus size={16} /> Nouveau salarie</button>
        </div>
      </div>
      <div className="card">
        {employees.length > 0 ? (
          <table className="data-table" data-testid="employees-table">
            <thead><tr><th>Nom</th><th>Email</th><th>Poste</th><th>Contrat</th><th>Entree</th><th>DPAE</th><th>Contrat</th><th></th></tr></thead>
            <tbody>
              {filteredEmployees.map(emp => (
                <tr key={emp.id}>
                  <td style={{ fontWeight: '700' }}>{emp.name}</td>
                  <td>{emp.email}</td>
                  <td>{emp.job_type || emp.job_title || '-'}</td>
                  <td><span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: emp.contract_type === 'CDD' ? '#FFF3E0' : '#E3F2FD', color: emp.contract_type === 'CDD' ? '#EF6C00' : '#1565C0' }}>{emp.contract_type}</span></td>
                  <td>{emp.entry_date || '-'}</td>
                  <td style={{ textAlign: 'center' }}><span style={{ color: emp.dpae_done ? '#2E7D32' : '#C62828', fontWeight: '600' }}>{emp.dpae_done ? 'Oui' : 'Non'}</span></td>
                  <td style={{ textAlign: 'center' }}><span style={{ color: emp.contract_signed ? '#2E7D32' : '#C62828', fontWeight: '600' }}>{emp.contract_signed ? 'Oui' : 'Non'}</span></td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button className="btn btn-secondary btn-sm" title="Carte/QR" onClick={async () => {
                        try {
                          const res = await axios.get(`${API_URL}/api/users/${emp.id}/badge`, { withCredentials: true, responseType: 'blob' });
                          window.open(window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })), '_blank');
                        } catch (e2) { toast.error('Erreur badge'); }
                      }} data-testid={`badge-emp-${emp.id}`}><IdentificationBadge size={14} /></button>
                      <button className="btn btn-secondary btn-sm" onClick={() => openEdit(emp)} data-testid={`edit-emp-${emp.id}`}><PencilSimple size={14} /></button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(emp.id, emp.name)}><Trash size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={{ padding: '40px', textAlign: 'center', color: '#5E5F66' }}><Users size={40} style={{ opacity: 0.5, marginBottom: '12px' }} /><p>Aucun salarie</p></div>
        )}
      </div>
    </>
    );
  };

  const renderCongesContent = () => {
    const ProgressBar = ({ used, total, color }) => {
      const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0;
      return (
        <div style={{ width: '100%', marginTop: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: '4px' }}>
            <span style={{ color: '#5E5F66' }}>{used}j pris</span>
            <span style={{ fontWeight: '700', color }}>{total - used}j restants</span>
          </div>
          <div style={{ height: '8px', backgroundColor: '#E2E2E6', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct}%`, backgroundColor: getProgressColor(pct), borderRadius: '4px', transition: 'width 0.6s ease' }} />
          </div>
        </div>
      );
    };

    return (
    <>
      {periodLabel && (
        <div style={{ marginBottom: '16px', padding: '8px 16px', backgroundColor: '#E3F2FD', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <CalendarCheck size={16} style={{ color: '#1565C0' }} />
          <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#1565C0' }}>Periode: {periodLabel}</span>
        </div>
      )}
      <div className="stats-grid" style={{ marginBottom: '24px', gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="stat-card"><div className="stat-label">Jours annuels</div><div className="stat-value">{leaveConfig.annual_days}</div></div>
        {isAdmin ? (
          <>
            <div className="stat-card"><div className="stat-label">En attente</div><div className="stat-value" style={{ color: '#EF6C00' }}>{pending.length}</div></div>
            <div className="stat-card"><div className="stat-label">Approuves (total)</div><div className="stat-value" style={{ color: '#2E7D32' }}>{approvedDays}j</div></div>
          </>
        ) : myBalance ? (
          <>
            <div className="stat-card">
              <div className="stat-label">Mon solde</div>
              <ProgressBar used={myBalance.used} total={myBalance.total} color="#2E7D32" />
            </div>
            <div className="stat-card"><div className="stat-label">Conges restants</div><div className="stat-value" style={{ color: myBalance.remaining > 5 ? '#2E7D32' : '#C62828' }}>{myBalance.remaining}j</div></div>
          </>
        ) : (
          <>
            <div className="stat-card"><div className="stat-label">Conges pris</div><div className="stat-value">-</div></div>
            <div className="stat-card"><div className="stat-label">Conges restants</div><div className="stat-value">-</div></div>
          </>
        )}
      </div>
      {isAdmin && balances.length > 0 && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <div className="card-header"><h3 className="card-title">Solde par salarie</h3></div>
          <div style={{ padding: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
              {filteredBalances.map(b => {
                const pct = b.total > 0 ? Math.min(100, (b.used / b.total) * 100) : 0;
                return (
                  <div key={b.user_id} style={{ border: '1px solid #E2E2E6', borderRadius: '8px', padding: '16px' }} data-testid={`balance-card-${b.user_id}`}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ fontWeight: '700', fontSize: '0.9rem' }}>{b.user_name}</span>
                      <span style={{ fontWeight: '700', color: b.remaining > 5 ? '#2E7D32' : '#C62828', fontSize: '1rem' }}>{b.remaining}j</span>
                    </div>
                    <div style={{ height: '6px', backgroundColor: '#E2E2E6', borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, backgroundColor: getProgressColor(pct), borderRadius: '3px', transition: 'width 0.6s ease' }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#5E5F66', marginTop: '4px' }}>
                      <span>{b.used}j pris</span>
                      <span>{b.total}j total</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {!isAdmin && myBalance?.history?.length > 0 && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <div className="card-header"><h3 className="card-title">Historique des conges pris</h3></div>
          <table className="data-table" data-testid="leave-history-table">
            <thead><tr><th>Du</th><th>Au</th><th>Jours</th><th>Motif</th></tr></thead>
            <tbody>
              {myBalance.history.map(h => (
                <tr key={h.id}>
                  <td>{h.start_date}</td>
                  <td>{h.end_date}</td>
                  <td style={{ fontWeight: '700' }}>{h.days}j</td>
                  <td>{h.reason || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '16px' }}>
        {isAdmin && <button className="btn btn-secondary" onClick={() => setShowConfigModal(true)} data-testid="config-btn"><Clock size={16} /> Configuration</button>}
        <button className="btn btn-primary" onClick={() => { setLeaveForm({ type: 'conge', start_date: '', end_date: '', reason: '' }); setAttestationFile(null); setShowLeaveModal(true); }} data-testid="create-leave-btn"><Plus size={16} /> Nouvelle demande</button>
      </div>
      <div className="card">
        {filteredLeaves.length > 0 ? renderLeaveTable(filteredLeaves, TYPE_CONGE, true) : (
          <div style={{ padding: '40px', textAlign: 'center', color: '#5E5F66' }}><CalendarCheck size={40} style={{ opacity: 0.5, marginBottom: '12px' }} /><p>Aucune demande de conge</p></div>
        )}
      </div>
    </>
    );
  };

  const renderMaladieContent = () => (
    <>
      <div className="stats-grid" style={{ marginBottom: '24px', gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="stat-card"><div className="stat-label">En attente</div><div className="stat-value" style={{ color: '#EF6C00' }}>{pending.length}</div></div>
        <div className="stat-card"><div className="stat-label">Arrets maladie</div><div className="stat-value" style={{ color: '#EF6C00' }}>{maladieDays}j</div></div>
        <div className="stat-card"><div className="stat-label">Absences</div><div className="stat-value" style={{ color: '#5E5F66' }}>{absenceDays}j</div></div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
        <button className="btn btn-primary" onClick={() => { setLeaveForm({ type: 'maladie', start_date: '', end_date: '', reason: '' }); setAttestationFile(null); setShowLeaveModal(true); }} data-testid="create-absence-btn"><Plus size={16} /> Nouvelle declaration</button>
      </div>
      <div className="card">
        {filteredLeaves.length > 0 ? renderLeaveTable(filteredLeaves, TYPE_MALADIE, true) : (
          <div style={{ padding: '40px', textAlign: 'center', color: '#5E5F66' }}><FirstAidKit size={40} style={{ opacity: 0.5, marginBottom: '12px' }} /><p>Aucun arret ou absence</p></div>
        )}
      </div>
    </>
  );

  const renderEmployeeModal = () => (
    <EmployeeFormModal
      show={showModal}
      onClose={() => { setShowModal(false); setEditMode(false); }}
      onSubmit={handleSubmit}
      form={form}
      setForm={setForm}
      editMode={editMode}
      showPassword={showPassword}
      setShowPassword={setShowPassword}
      allProjects={allProjects}
    />
  );

  const pending = leaves.filter(l => l.status === 'en_attente');

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  // For embedded mode (used within ADMINISTRATION page), skip header/tabs
  // The mainTab is already set via embeddedTab prop
  // We also handle 'salaries_merged' which maps to 'documents' (employee cards + detail)
  if (embeddedTab) {
    // Detail view for selected employee
    if ((mainTab === 'documents' || mainTab === 'salaries') && selectedEmployee && !showModal) {
      return renderEmployeeDetail();
    }
    return (
      <div data-testid="employees-embedded">
        {/* salaries_merged: shows employee cards (documents tab) or salaries table */}
        {mainTab === 'documents' && isAdmin && renderEmployeeCards()}
        {mainTab === 'salaries' && isAdmin && renderSalariesTable()}
        {mainTab === 'conges' && renderCongesContent()}
        {mainTab === 'maladie' && renderMaladieContent()}
        {showModal && renderEmployeeModal()}
        <LeaveRequestModal show={showLeaveModal} onClose={() => setShowLeaveModal(false)} onSubmit={handleCreateLeave} form={leaveForm} setForm={setLeaveForm} isMaladie={mainTab === 'maladie'} attestationFile={attestationFile} setAttestationFile={setAttestationFile} />
        <LeaveConfigModal show={showConfigModal} onClose={() => setShowConfigModal(false)} onSubmit={handleSaveConfig} config={leaveConfig} setConfig={setLeaveConfig} />
      </div>
    );
  }

  const TABS = isAdmin
    ? [
        { key: 'salaries', label: t('tab.employees_list'), icon: Users },
        { key: 'documents', label: t('tab.employee_docs'), icon: Folder },
        { key: 'conges', label: t('tab.leaves'), icon: CalendarCheck },
        { key: 'maladie', label: t('tab.absences'), icon: FirstAidKit },
      ]
    : [
        { key: 'conges', label: t('tab.leaves'), icon: CalendarCheck },
        { key: 'maladie', label: t('tab.absences'), icon: FirstAidKit },
      ];


  // Detail view (non-embedded + embedded)
  if ((mainTab === 'documents' || mainTab === 'salaries') && selectedEmployee && !showModal) {
    return renderEmployeeDetail();
  }

  // Render leave table using extracted component
  const renderLeaveTable = (items, typeMap) => (
    <LeaveTable items={items} typeMap={typeMap} isAdmin={isAdmin} mainTab={mainTab} onAction={handleLeaveAction} onDelete={handleDeleteLeave} badge={badge} statusMap={STATUS_LEAVE} />
  );

  // Employee standalone page (non-embedded)
  return (
    <div data-testid="employees-page">
      <div className="page-header">
        <h1 className="page-title">{isAdmin ? t('page.employees') : t('nav.leaves_absences')}</h1>
        <p className="page-subtitle">{isAdmin ? `${employees.length} salarie(s)` : ''}</p>
      </div>

      <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid #E2E2E6', marginBottom: '24px' }}>
        {TABS.map(tab => (
          <button key={tab.key} onClick={() => { setMainTab(tab.key); setSelectedEmployee(null); }}
            data-testid={`tab-${tab.key}`}
            style={{
              padding: '12px 24px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer',
              fontWeight: mainTab === tab.key ? '700' : '400',
              borderBottom: mainTab === tab.key ? '2px solid var(--primary, #002FA7)' : '2px solid transparent',
              marginBottom: '-2px', color: mainTab === tab.key ? 'var(--primary, #002FA7)' : '#5E5F66',
              display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem'
            }}>
            <tab.icon size={18} /> {tab.label}
          </button>
        ))}
      </div>

      {mainTab === 'salaries' && isAdmin && renderSalariesTable()}
      {mainTab === 'documents' && isAdmin && renderEmployeeCards()}
      {mainTab === 'conges' && renderCongesContent()}
      {mainTab === 'maladie' && renderMaladieContent()}
      {showModal && renderEmployeeModal()}
      <LeaveRequestModal show={showLeaveModal} onClose={() => setShowLeaveModal(false)} onSubmit={handleCreateLeave} form={leaveForm} setForm={setLeaveForm} isMaladie={mainTab === 'maladie'} attestationFile={attestationFile} setAttestationFile={setAttestationFile} />
      <LeaveConfigModal show={showConfigModal} onClose={() => setShowConfigModal(false)} onSubmit={handleSaveConfig} config={leaveConfig} setConfig={setLeaveConfig} />
    </div>
  );
};

export default EmployeesPage;
